import { InviteMemberDialog } from '@/components/team/InviteMemberDialog';
import {
  assignableRoles,
  INVITATIONS_QUERY_KEY,
  isAdminRole,
  MEMBERS_QUERY_KEY,
  ROLE_PINNED_MESSAGE,
  roleLabel,
  type InvitationsResponse,
  type MembersResponse,
  type TeamInvitationDto,
  type TeamMemberDto,
} from '@/components/team/team-types';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyTableState,
  PageContainer,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Plus, Users } from 'lucide-react';
import { useState } from 'react';

function formatDate(timestamp: number | null): string {
  if (!timestamp) return '—';
  return new Date(timestamp).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Team management for the active organization: current members (role changes,
 * removal) and pending invitations (invite, revoke). Members can view the
 * roster; every mutation — and the invitations list — is admin-only and
 * enforced again server-side.
 */
export function TeamPage() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamMemberDto | null>(null);
  const [invitationToRevoke, setInvitationToRevoke] = useState<TeamInvitationDto | null>(null);
  const [invitationToResend, setInvitationToResend] = useState<TeamInvitationDto | null>(null);

  const membersQuery = useQuery({
    queryKey: MEMBERS_QUERY_KEY,
    queryFn: () => apiClient.get<MembersResponse>('/organizations/members'),
  });

  const members = membersQuery.data?.data?.members ?? [];
  const callerRole = membersQuery.data?.data?.callerRole;
  const isInternalOrg = membersQuery.data?.data?.isInternalOrg ?? false;
  const callerIsAdmin = isAdminRole(callerRole);

  const invitationsQuery = useQuery({
    queryKey: INVITATIONS_QUERY_KEY,
    queryFn: () => apiClient.get<InvitationsResponse>('/organizations/invitations'),
    enabled: callerIsAdmin,
  });
  const invitations = invitationsQuery.data?.data ?? [];

  const changeRoleMutation = useMutation({
    mutationFn: async ({ member, role }: { member: TeamMemberDto; role: string }) =>
      apiClient.patch<{ success: boolean; data: { role: string; rolePinned: boolean } }>(
        `/organizations/members/${member.userId}`,
        { role },
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
      if (response.data?.rolePinned) {
        toast.success(`Role stays ${roleLabel(response.data.role)}. ${ROLE_PINNED_MESSAGE}.`);
      } else {
        toast.success('Role updated');
      }
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to update role');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (member: TeamMemberDto) =>
      apiClient.delete(`/organizations/members/${member.userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
      toast.success('Member removed');
      setMemberToRemove(null);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to remove member');
      setMemberToRemove(null);
    },
  });

  const resendInvitationMutation = useMutation({
    mutationFn: async (invitation: TeamInvitationDto) =>
      apiClient.post<{ success: boolean; data: { linkRotated: boolean } }>(
        `/organizations/invitations/${invitation.id}/resend`,
        {},
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY });
      if (response.data?.linkRotated) {
        toast.success('Invitation email resent with a new link; the old link no longer works');
      } else {
        toast.success('Invitation email resent');
      }
      setInvitationToResend(null);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to resend invitation');
      setInvitationToResend(null);
    },
  });

  const revokeInvitationMutation = useMutation({
    mutationFn: async (invitation: TeamInvitationDto) =>
      apiClient.delete(`/organizations/invitations/${invitation.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY });
      toast.success('Invitation revoked');
      setInvitationToRevoke(null);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to revoke invitation');
      setInvitationToRevoke(null);
    },
  });

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Team</h1>
            <p className="text-sm text-muted-foreground">
              Manage who can access your organization on Destination Hunts.
            </p>
          </div>
          {callerIsAdmin && (
            <div className="flex sm:justify-end gap-3">
              <Button onClick={() => setInviteOpen(true)} icon={Plus}>
                Invite member
              </Button>
            </div>
          )}
        </div>

        {/* Members */}
        <Card className="gap-0 py-0">
          <div className="border-b px-6 py-4">
            <h2 className="text-lg font-medium tracking-tight">Members</h2>
            <p className="text-sm text-muted-foreground">
              Everyone with access to this organization.
            </p>
          </div>
          <CardContent className="p-0">
            {membersQuery.isLoading ? (
              <div className="space-y-3 p-6">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : members.length === 0 ? (
              <EmptyTableState
                icon={<Users className="h-8 w-8" />}
                title="No members yet"
                description="Invite teammates to collaborate on hunts."
              />
            ) : (
              <ul className="divide-y">
                {members.map((member) => (
                  <li
                    key={member.userId}
                    className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">
                        {member.name || member.email || 'Unknown user'}
                        {member.isSelf && (
                          <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                        )}
                      </p>
                      <p className="truncate text-sm text-muted-foreground">{member.email}</p>
                    </div>
                    <div className="hidden text-sm text-muted-foreground sm:block sm:w-28">
                      {formatDate(member.joinedAt)}
                    </div>
                    <div className="flex items-center justify-end gap-3">
                      {callerIsAdmin && !member.isSelf ? (
                        <>
                          <Select
                            value={member.role}
                            onValueChange={(role: string) =>
                              changeRoleMutation.mutate({ member, role })
                            }
                          >
                            <SelectTrigger
                              className="w-36"
                              aria-label={`Role for ${member.name || member.email}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {assignableRoles(isInternalOrg).map((option) => (
                                <SelectItem key={option} value={option}>
                                  {roleLabel(option)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMemberToRemove(member)}
                          >
                            Remove
                          </Button>
                        </>
                      ) : (
                        <Badge variant="secondary">{roleLabel(member.role)}</Badge>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Pending invitations (admin only) */}
        {callerIsAdmin && (
          <Card className="gap-0 py-0">
            <div className="border-b px-6 py-4">
              <h2 className="text-lg font-medium tracking-tight">Pending invitations</h2>
              <p className="text-sm text-muted-foreground">
                Invitations that haven&apos;t been accepted yet.
              </p>
            </div>
            <CardContent className="p-0">
              {invitationsQuery.isLoading ? (
                <div className="space-y-3 p-6">
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : invitations.length === 0 ? (
                <EmptyTableState
                  icon={<Mail className="h-8 w-8" />}
                  title="No pending invitations"
                  description="Invitations you send will appear here until they're accepted."
                  action={{
                    label: 'Invite member',
                    onClick: () => setInviteOpen(true),
                    icon: Plus,
                  }}
                />
              ) : (
                <ul className="divide-y">
                  {invitations.map((invitation) => (
                    <li
                      key={invitation.id}
                      className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{invitation.email}</p>
                        <p className="text-sm text-muted-foreground">
                          Invited {formatDate(invitation.createdAt)}
                          {invitation.expiresAt
                            ? ` · Expires ${formatDate(invitation.expiresAt)}`
                            : ''}
                        </p>
                      </div>
                      <div className="flex items-center justify-end gap-3">
                        <Badge variant="secondary">{roleLabel(invitation.role)}</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={resendInvitationMutation.isPending}
                          onClick={() => setInvitationToResend(invitation)}
                        >
                          Resend
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setInvitationToRevoke(invitation)}
                        >
                          Revoke
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        isInternalOrg={isInternalOrg}
      />

      <ConfirmDialog
        open={memberToRemove !== null}
        onOpenChange={(open: boolean) => {
          if (!open) setMemberToRemove(null);
        }}
        title="Remove member"
        description={`Remove ${memberToRemove?.name || memberToRemove?.email || 'this member'} from the organization? They will lose access immediately.`}
        confirmText="Remove"
        variant="destructive"
        loading={removeMemberMutation.isPending}
        onConfirm={() => {
          if (memberToRemove) removeMemberMutation.mutate(memberToRemove);
        }}
      />

      <ConfirmDialog
        open={invitationToResend !== null}
        onOpenChange={(open: boolean) => {
          if (!open) setInvitationToResend(null);
        }}
        title="Resend invitation"
        description={`Send another invitation email to ${invitationToResend?.email ?? 'this invitee'}?`}
        confirmText="Resend"
        loading={resendInvitationMutation.isPending}
        onConfirm={() => {
          if (invitationToResend) resendInvitationMutation.mutate(invitationToResend);
        }}
      />

      <ConfirmDialog
        open={invitationToRevoke !== null}
        onOpenChange={(open: boolean) => {
          if (!open) setInvitationToRevoke(null);
        }}
        title="Revoke invitation"
        description={`Revoke the invitation for ${invitationToRevoke?.email ?? 'this invitee'}? Their invitation link will stop working.`}
        confirmText="Revoke"
        variant="destructive"
        loading={revokeInvitationMutation.isPending}
        onConfirm={() => {
          if (invitationToRevoke) revokeInvitationMutation.mutate(invitationToRevoke);
        }}
      />
    </PageContainer>
  );
}
